// Installer Network — professional and prepared, while the NZ network is being developed.
import { Link } from "react-router-dom";
import {
  ArrowRight, Network, ShieldCheck, MessageCircle, HeartHandshake,
  Wrench, CalendarClock, Sparkles, MapPin, Handshake, Building2,
} from "lucide-react";
import Reveal from "@/components/layout/Reveal";

const NETWORK_PARTNERS = [
  {
    name: "Simply Plumbing",
    logo: "https://customer-assets-0z36b82j.emergentagent.net/job_aero-launch/artifacts/ivbprdjz_Simply-plumbing-logo-.png",
    url: "https://simplyplumbing.co.nz/",
    dark: false,
  },
  {
    name: "BDF Electrical Ltd",
    logo: "https://customer-assets-0z36b82j.emergentagent.net/job_aero-launch/artifacts/yvyfev8o_BDF%20Logo.webp",
    url: "https://www.bdfelectrical.co.nz/",
    dark: false,
  },
  {
    name: "Fast Flow Plumbing",
    logo: "https://customer-assets-0z36b82j.emergentagent.net/job_aero-launch/artifacts/yj8iolfb_Fast_Flow_Plumbing_logo.webp",
    url: "https://fastflow.co.nz/",
    dark: true, // logo has white elements — needs dark backing tile
  },
];

const VALUES = [
  { icon: Wrench,         t: "Professional workmanship", b: "We prioritise tidy, careful installations done to a consistent standard." },
  { icon: MessageCircle,  t: "Clear communication",      b: "Customers know what to expect, when — from quote to commissioning." },
  { icon: HeartHandshake, t: "Customer-first service",   b: "A respectful, helpful, no-pressure experience from start to finish." },
  { icon: Sparkles,       t: "Product familiarity",      b: "Installer partners are trained on the NeoPower Black Shield and AeroMax standards." },
  { icon: CalendarClock,  t: "Reliable scheduling",      b: "Confirmed bookings, on-time arrivals, and clear progress updates." },
  { icon: ShieldCheck,    t: "Clean & safe install standards", b: "Compliant, tidy, and respectful of your home — every install, every time." },
];

const REGIONS = ["Auckland", "Waikato", "Bay of Plenty", "Hawke's Bay", "Manawatū", "Wellington", "Canterbury"];

function Hero() {
  return (
    <section data-testid="network-hero" className="relative overflow-hidden bg-[#0B132B] text-white">
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)", backgroundSize: "44px 44px" }}
      />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[440px] w-[660px] rounded-full bg-[#10B981]/12 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 pt-16 pb-20 sm:pt-20 sm:pb-24">
        <Reveal>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-[10px] tracking-[0.22em] uppercase font-semibold text-white/85">
            <Network className="h-3 w-3 text-[#10B981]" /> Installer Network
          </span>
        </Reveal>
        <Reveal delay={80}>
          <h1 className="font-heading mt-5 text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.06] max-w-4xl">
            Building a trusted installer network <span className="text-[#10B981]">across New Zealand.</span>
          </h1>
        </Reveal>
        <Reveal delay={140}>
          <p className="mt-6 text-lg sm:text-xl text-white/80 leading-relaxed max-w-2xl">
            AeroMax Energy is developing a trusted network of experienced hot water heat pump installers and plumbing professionals to support quality, consistency and reliable customer outcomes.
          </p>
        </Reveal>
        <Reveal delay={200}>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/free-home-assessment" data-testid="network-cta-assess" className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-7 py-3.5 font-semibold transition-all hover:shadow-xl hover:shadow-emerald-500/30">
              Request Free Assessment <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/become-installer-partner" data-testid="network-cta-partner" className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 backdrop-blur border border-white/20 text-white px-7 py-3.5 font-semibold transition-all">
              Become an Installer Partner
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function InstallerNetwork() {
  return (
    <div className="bg-white">
      <Hero />

      {/* Approach */}
      <section data-testid="network-approach" className="py-20 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 grid md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-6">
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#10B981]">Our approach</span>
            <h2 className="font-heading mt-3 text-3xl sm:text-5xl font-bold text-[#0B132B] leading-[1.05]">
              Connecting Kiwi homes to <span className="text-[#10B981]">qualified installation support.</span>
            </h2>
            <p className="mt-5 text-lg text-[#475569] leading-relaxed">
              AeroMax aims to connect customers with suitable installation support through a growing network of experienced professionals — so every home gets a consistent, careful upgrade experience.
            </p>
            <p className="mt-3 text-[#475569] leading-relaxed">
              Our coordination team works alongside installer partners from initial assessment through to commissioning and customer handover.
            </p>
          </Reveal>
          <Reveal delay={120} className="md:col-span-6">
            <div className="grid grid-cols-2 gap-3">
              {[
                { v: "NZ-wide", l: "Growth plan" },
                { v: "Vetted",  l: "Partners" },
                { v: "AeroMax", l: "Coordination" },
                { v: "End-to-end", l: "Customer support" },
              ].map((s, i) => (
                <div key={i} className="rounded-2xl bg-[#F8FAFC] border border-[#E2E8F0] p-5">
                  <p className="font-heading text-2xl font-bold text-[#0B132B] leading-none num-tab">{s.v}</p>
                  <p className="mt-2 text-[10px] uppercase tracking-[0.18em] font-bold text-[#10B981]">{s.l}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section data-testid="network-values" className="py-20 sm:py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <Reveal className="max-w-3xl">
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#10B981]">What matters to us</span>
            <h2 className="font-heading mt-3 text-3xl sm:text-5xl font-bold text-[#0B132B] leading-[1.05]">
              The standards behind every <span className="text-[#10B981]">AeroMax install.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {VALUES.map((v, i) => (
              <Reveal key={i} delay={i * 60}>
                <div className="h-full rounded-2xl bg-white border border-[#E2E8F0] p-7 hover:border-[#10B981]/40 hover:shadow-lg hover:shadow-emerald-500/8 hover:-translate-y-0.5 transition-all">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#10B981]/12 text-[#10B981] ring-1 ring-[#10B981]/20">
                    <v.icon className="h-5 w-5" strokeWidth={2} />
                  </span>
                  <h3 className="font-heading mt-5 text-lg font-bold text-[#0B132B] leading-snug">{v.t}</h3>
                  <p className="mt-2 text-sm text-[#475569] leading-relaxed">{v.b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Regional growth */}
      <section data-testid="network-regions" className="py-20 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 text-center">
          <Reveal>
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#10B981]">Regional growth</span>
            <h2 className="font-heading mt-3 text-3xl sm:text-5xl font-bold text-[#0B132B] leading-[1.05]">
              Expanding installation support <span className="text-[#10B981]">region by region.</span>
            </h2>
            <p className="mt-5 text-lg text-[#475569] leading-relaxed max-w-2xl mx-auto">
              AeroMax is working toward broader installation support across key New Zealand regions as demand grows.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <div className="mt-10 flex flex-wrap justify-center gap-2.5">
              {REGIONS.map((r) => (
                <span key={r} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F8FAFC] border border-[#E2E8F0] text-sm font-semibold text-[#0B132B] hover:border-[#10B981]/40 hover:bg-[#10B981]/5 transition-colors">
                  <MapPin className="h-3.5 w-3.5 text-[#10B981]" /> {r}
                </span>
              ))}
            </div>
            <p className="mt-6 text-xs text-[#475569]">
              More regions launching soon. Live installer-locator coming as our network expands.
            </p>
          </Reveal>

          {/* Trusted partner logo strip */}
          <Reveal delay={200}>
            <div data-testid="network-partner-strip" className="mt-14 sm:mt-16">
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#10B981]">
                Trusted installation partners supporting active regions
              </p>
              <p className="mt-3 text-sm text-[#475569] leading-relaxed max-w-2xl mx-auto">
                Plumbing and electrical partner availability may vary by region, job requirements and final site assessment.
              </p>

              {/* Desktop: subtle marquee */}
              <div
                data-testid="partner-marquee"
                className="hidden md:block mt-8 relative overflow-hidden rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] py-6"
                style={{
                  maskImage:
                    "linear-gradient(to right, transparent 0, black 8%, black 92%, transparent 100%)",
                  WebkitMaskImage:
                    "linear-gradient(to right, transparent 0, black 8%, black 92%, transparent 100%)",
                }}
              >
                <div className="partner-marquee-track flex w-max items-center gap-12">
                  {[...NETWORK_PARTNERS, ...NETWORK_PARTNERS, ...NETWORK_PARTNERS, ...NETWORK_PARTNERS].map((p, idx) => (
                    <a
                      key={`marquee-${idx}`}
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Visit ${p.name} website (opens in new tab)`}
                      className="shrink-0 flex items-center justify-center px-5"
                    >
                      <div
                        className={`flex h-16 w-44 items-center justify-center rounded-xl px-4 transition-opacity hover:opacity-100 ${
                          p.dark
                            ? "bg-[#0B132B]"
                            : "bg-white border border-[#E2E8F0]"
                        }`}
                      >
                        <img
                          src={p.logo}
                          alt={`${p.name} logo`}
                          loading="lazy"
                          className="max-h-10 max-w-[80%] object-contain"
                        />
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* Tablet/Mobile: static logo row */}
              <div
                data-testid="partner-strip-static"
                className="md:hidden mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3"
              >
                {NETWORK_PARTNERS.map((p) => (
                  <a
                    key={`static-${p.name}`}
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Visit ${p.name} website (opens in new tab)`}
                    className={`flex h-20 items-center justify-center rounded-xl px-4 border ${
                      p.dark
                        ? "bg-[#0B132B] border-[#0B132B]"
                        : "bg-white border-[#E2E8F0]"
                    }`}
                  >
                    <img
                      src={p.logo}
                      alt={`${p.name} logo`}
                      loading="lazy"
                      className="max-h-12 max-w-[80%] object-contain"
                    />
                  </a>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* For customers + dual CTA */}
      <section data-testid="network-customer" className="py-16 sm:py-20 bg-[#F8FAFC]">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 grid md:grid-cols-2 gap-5">
          <Reveal>
            <div className="h-full rounded-3xl bg-white border border-[#E2E8F0] p-7 sm:p-9 shadow-sm">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#10B981]/12 text-[#059669] text-[10px] font-bold tracking-[0.18em] uppercase">
                <Building2 className="h-3 w-3" /> For customers
              </span>
              <h3 className="font-heading mt-3 text-2xl font-bold text-[#0B132B] leading-tight">Start with a free assessment</h3>
              <p className="mt-3 text-[#475569] leading-relaxed">
                Customers can begin with a free assessment and AeroMax will guide the next steps — matching you with suitable installation support in your region.
              </p>
              <Link to="/free-home-assessment" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-6 py-3 font-semibold transition-all">
                Request Free Assessment <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="h-full rounded-3xl bg-[#0B132B] text-white p-7 sm:p-9 shadow-sm">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 backdrop-blur text-white text-[10px] font-bold tracking-[0.18em] uppercase">
                <Handshake className="h-3 w-3 text-[#10B981]" /> For installers
              </span>
              <h3 className="font-heading mt-3 text-2xl font-bold leading-tight">Join the AeroMax network</h3>
              <p className="mt-3 text-white/75 leading-relaxed">
                Are you an experienced plumber or hot water installer? Apply to join our growing partner network as we expand across New Zealand.
              </p>
              <Link to="/become-installer-partner" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 backdrop-blur border border-white/20 text-white px-6 py-3 font-semibold transition-all">
                Become an Installer Partner <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
